/**
 * VoiceRecorder — tap-to-record on a desk, CONVERSATION MODE in the room.
 *
 * I-03 R6 · S3-06.3 (proposal §3f). Operator ruling: "hands are free-ish…
 * sometimes wet, with gloves." So in the dock this is not a
 * press-and-hold button any more: ONE TAP opens a listening session that
 * stays open across turns. A voice-activity detector segments what is
 * said, each segment goes to the existing Whisper endpoint as a turn, the
 * reply renders, and the mic re-arms. The keeper does not touch the
 * screen again until they want it to stop.
 *
 * The state machine is `listeningSession.js` and has no audio in it; this
 * file is the audio and the pixels. That split is what lets the two-minute
 * silence close, the pause-on-background and the re-arm be tested without
 * a microphone.
 *
 * ── THE VAD IS LAZY, AND THAT IS A BUDGET DECISION ────────────────────
 *
 * Silero via `@ricky0123/vad-web` is WebAssembly (onnxruntime-web) plus a
 * ~2.3 MB model. It is `import()`ed ONLY when a session actually starts,
 * so a keeper who never taps the mic downloads none of it and the main
 * bundle does not grow. An energy threshold was considered and REFUSED
 * by the proposal: the room has misters and fans in it, and a threshold
 * detector in that room hears a turn every few seconds.
 *
 * Its assets are served from OUR origin (`/vad/`), copied out of the two
 * packages by `scripts/copy-vad-assets.js` at build time — never from a
 * CDN, which would put a third party in the path of the microphone.
 *
 * ── iOS SAFARI, WHICH IS THE DEVICE THIS IS FOR ───────────────────────
 *
 *  * `getUserMedia` needs ONE user gesture per page load. The tap that
 *    opens the session is it; everything after is the session staying
 *    open.
 *  * The stream is suspended on screen lock or backgrounding, so
 *    `visibilitychange` PAUSES and says "tap to resume". It never claims
 *    to be listening while the OS holds the mic.
 *  * MediaRecorder emits `audio/mp4` (AAC), not webm — the container is
 *    chosen by asking `isTypeSupported`, mp4 first. Whisper accepts m4a.
 *
 * ⚠️ UNVERIFIED ON DEVICE. The iPad has not been acquired
 * (device ruling 2026-09-10) and NOTHING here has run on one. The
 * BLUETOOTH CLICKER below is designed, not promised: only a remote in
 * KEYBOARD mode (Enter/Space) can be seen by a page at all — an
 * iOS-mode shutter remote sends Volume Up, which a web page cannot
 * observe — and whether this particular mapping survives iPadOS's focus
 * and software-keyboard behaviour is a question for the device.
 */

import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Alert, Spinner } from 'react-bootstrap';
import {
  ENDED, HEARING, IDLE, LISTENING, PAUSED, SILENCE_TIMEOUT_MS, STARTING,
  STATE_LABELS, THINKING, createListeningSession, pickMimeType,
} from './listeningSession';
import { encodeWav, secondsOf } from './wavEncoder';
import './VoiceRecorder.css';

/** Where `scripts/copy-vad-assets.js` puts the model, worklet and wasm. */
export const VAD_ASSET_PATH = '/vad/';

/**
 * The lazy load, in its own function so a test can assert it has NOT
 * been called at mount and so the failure is one place.
 */
export const loadVad = () => import(
  /* webpackChunkName: "frogger-vad" */ '@ricky0123/vad-web'
);

// ═══════════════════════════════════════════════════════════════════════
// THE VOICE TEST HOOK (I-03 R6 · S3-06.3c) — COMPILED OUT OF PROD
// ═══════════════════════════════════════════════════════════════════════
//
// The iPad was not acquired (device ruling 2026-09-10), so conversation
// mode is simulated "as closely as we can" instead. The problem it
// solves: NOTHING past the first tap of the mic could be driven by a
// test. Chromium's fake capture device emits a tone and Silero correctly
// hears no speech in it; WebKit has no fake device at all. So the whole
// daily loop — the transcript bubble, the turn, the read-back, Undo, the
// re-arm, the silence close — was unreachable by the harness AND by
// Cowork on beta, which is the half of QA that finds what unit tests
// cannot.
//
// WHAT IS FAKED, and it is exactly two things:
//   1. `getUserMedia` — a REAL MediaStream carrying silence, built from
//      an AudioContext oscillator at zero gain. A real stream, because
//      everything downstream holds one and stops its tracks; silent,
//      because nothing listens to it.
//   2. THE DETECTOR. Silero is replaced by an object with the same three
//      callbacks, fired on demand by `window.__froggerVoiceTest.emit()`
//      with the samples of a canned clip from `/voice-test/<name>.wav`.
//
// WHAT STAYS REAL — everything else, deliberately, because everything
// else is what can be wrong: the session state machine, `wavEncoder`,
// the multipart upload with `place`, Whisper, the graph, the tools, the
// writes, `applyChunk`, the read-back row, Undo, the navigate chunk.
// A simulation that also faked the turn would prove nothing at all.
//
// WHAT IT STILL CANNOT SIMULATE: whether Silero segments the operator's
// actual voice in a room with misters running, whether iPadOS holds the
// mic session across a screen lock, and the Bluetooth clicker. Those are
// the device's to answer.
//
// ── THE GATE IS AT BUILD TIME, AND IT IS WRITTEN OUT LONGHAND ─────────
//
// `process.env.REACT_APP_VOICE_TEST_HOOK === '1'` is substituted by
// DefinePlugin with a string LITERAL (craco.config.js defines it
// explicitly, defaulting to '0', so an UNSET variable is still a literal
// and still folds). Webpack then drops the dead branch at parse time,
// taking every string inside it — `/voice-test/`, `__froggerVoiceTest`
// — with it. That is why each entry point below repeats the condition in
// full rather than reading a shared constant: a module-level
// `const ENABLED = …` is not something the minifier is guaranteed to
// inline, and "probably eliminated" is not a gate.

/** The tester's own object, or null. Null in any build without the flag. */
export function voiceTestHook() {
  if (process.env.REACT_APP_VOICE_TEST_HOOK !== '1') return null;
  if (typeof window === 'undefined') return null;
  return window.__froggerVoiceTest || null;
}

/**
 * The silence clock, as a FUNCTION so the hook can shorten it.
 *
 * The harness cannot wait two real minutes to prove the session closes
 * itself, and `window.__froggerVoiceTest.silenceMs` is not known when
 * this component first renders — which is when the session is built.
 * Reading it at ARM time is what makes it settable from the outside.
 */
export function voiceTestSilenceMs() {
  if (process.env.REACT_APP_VOICE_TEST_HOOK !== '1') return SILENCE_TIMEOUT_MS;
  const hook = voiceTestHook();
  const n = Number(hook && hook.silenceMs);
  return Number.isFinite(n) && n > 0 ? n : SILENCE_TIMEOUT_MS;
}

/**
 * Build the fake stream + fake detector and wire `emit` onto the hook.
 *
 * Returns something shaped exactly like the `MicVAD` instance the real
 * path builds — `start` / `pause` / `destroy` — plus the `stream` the
 * session is to hold; or null when the gate is off or no hook is set.
 *
 * `callbacks` is THE SAME OBJECT handed to `MicVAD.new`. Not a copy and
 * not a re-declaration: if the fake fired a different `onSpeechEnd` than
 * the real detector does, the simulation would be exercising a code path
 * the operator never runs, which is worse than no simulation.
 */
export function createVoiceTestVad(hook, callbacks) {
  if (process.env.REACT_APP_VOICE_TEST_HOOK !== '1') return null;
  if (!hook) return null;

  // ── 1. A REAL MediaStream, carrying silence ────────────────────────
  //
  // Not a stub object: `stopTracks` calls `getTracks().forEach(stop)` on
  // it and the component's teardown has to keep working unchanged. Gain
  // 0 because the detector is faked — audio nothing listens to.
  const makeStream = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      const dest = ctx.createMediaStreamDestination();
      osc.connect(gain);
      gain.connect(dest);
      osc.start();
      return {
        stream: dest.stream,
        close: () => {
          try { osc.stop(); } catch (e) { /* already stopped */ }
          try { ctx.close(); } catch (e) { /* already closed */ }
        },
      };
    } catch (err) { /* WebKit without a gesture, or no AudioContext */ }
    try {
      // A canvas capture stream is a real MediaStream too, with no audio
      // in it — and nothing downstream reads the audio.
      const canvas = document.createElement('canvas');
      canvas.width = 2;
      canvas.height = 2;
      return { stream: canvas.captureStream(1), close: () => {} };
    } catch (err) { /* no captureStream either */ }
    // Neither: jsdom, which has no audio subsystem at all. `stopTracks`
    // wants `getTracks()` and nothing else does anything with it.
    return { stream: { getTracks: () => [] }, close: () => {} };
  };
  const { stream, close } = makeStream();

  // ── 2. The clip, decoded to what `onSpeechEnd` really hands back ───
  //
  // Parsed by hand rather than through `decodeAudioData`, because the
  // detector's contract is 16 kHz mono Float32 and `decodeAudioData`
  // resamples to whatever rate the AudioContext happens to be running at
  // (48 kHz on most machines). A clip resampled to 48 k and then
  // labelled 16 k by `wavEncoder` is a chipmunk Whisper cannot read. The
  // clips are generated as 16 kHz mono PCM16 and this refuses anything
  // else out loud.
  const decodeClip = async (name) => {
    const res = await fetch(`/voice-test/${encodeURIComponent(name)}.wav`);
    if (!res.ok) throw new Error(`voice-test clip ${name}: HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    const view = new DataView(buf);
    const tag = (off) => String.fromCharCode(
      view.getUint8(off), view.getUint8(off + 1),
      view.getUint8(off + 2), view.getUint8(off + 3));
    if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') {
      throw new Error(`voice-test clip ${name} is not a WAV`);
    }
    let off = 12;
    let fmt = null;
    let data = null;
    while (off + 8 <= buf.byteLength) {
      const id = tag(off);
      const size = view.getUint32(off + 4, true);
      if (id === 'fmt ') {
        fmt = {
          format: view.getUint16(off + 8, true),
          channels: view.getUint16(off + 10, true),
          rate: view.getUint32(off + 12, true),
          bits: view.getUint16(off + 22, true),
        };
      } else if (id === 'data') {
        data = { start: off + 8, size: Math.min(size, buf.byteLength - off - 8) };
      }
      off += 8 + size + (size % 2);
    }
    if (!fmt || !data) throw new Error(`voice-test clip ${name}: no fmt/data chunk`);
    if (fmt.format !== 1 || fmt.channels !== 1 || fmt.bits !== 16 || fmt.rate !== 16000) {
      throw new Error(`voice-test clip ${name} must be 16 kHz mono PCM16, got `
        + `${fmt.rate} Hz / ${fmt.channels} ch / ${fmt.bits}-bit / fmt ${fmt.format}`);
    }
    const count = Math.floor(data.size / 2);
    const out = new Float32Array(count);
    for (let i = 0; i < count; i += 1) {
      const v = view.getInt16(data.start + i * 2, true);
      out[i] = v < 0 ? v / 0x8000 : v / 0x7fff;
    }
    return out;
  };

  let live = true;

  // THE HOOK'S PUBLIC SURFACE. `emit` resolves only once the segment has
  // been handed over, so a driver can await it and then read the DOM
  // rather than polling for a race it caused itself.
  hook.emit = async (name) => {
    if (!live) throw new Error('voice test: no session is open');
    const samples = await decodeClip(name);
    callbacks.onSpeechStart();
    callbacks.onSpeechEnd(samples);
    return { name, samples: samples.length, seconds: samples.length / 16000 };
  };
  // A mister, a door — the detector deciding that was not speech after
  // all. Not a turn, and the silence clock restarts.
  hook.misfire = () => {
    if (!live) return false;
    callbacks.onVADMisfire();
    return true;
  };
  hook.ready = true;

  return {
    stream,
    start: async () => {},
    pause: () => {},
    destroy: () => {
      live = false;
      hook.ready = false;
      delete hook.emit;
      delete hook.misfire;
      close();
    },
  };
}

const VoiceRecorder = ({
  onVoiceMessage,
  onStatusChange,
  disabled = false,
  compact = false,
  conversation,
  maxDurationSeconds = 300,
  recommendedMaxSeconds = 75,
  // Batch 34 C4 (Cowork run 32): ONE primary per surface. On the customer
  // FroggyAI surfaces (the bubble, /account/froggy-chat) the surface's
  // primary is SEND, so the mic at rest is the family SECONDARY. The
  // keeper's dock keeps the default — there the mic is the act.
  variant = 'primary',
}) => {
  // Conversation mode is the dock's default; a desk keeps tap-to-record.
  const wantsConversation = conversation === undefined ? compact : conversation;

  const [sessionState, setSessionState] = useState(IDLE);
  const [endReason, setEndReason] = useState(null);
  const [error, setError] = useState(null);
  const [vadUnavailable, setVadUnavailable] = useState(false);

  // --- tap-to-record (the desk path, unchanged in behaviour) ---------
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const streamRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const vadRef = useRef(null);
  const sessionRef = useRef(null);
  const rootRef = useRef(null);
  const micBtnRef = useRef(null);
  // Push-to-talk state, synchronous: a clicker repeats faster than React
  // re-renders, and the VAD's own callbacks are async.
  const pttRef = useRef(false);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const teardownVad = useCallback(() => {
    if (vadRef.current) {
      try { vadRef.current.pause?.(); } catch (e) { /* already gone */ }
      try { vadRef.current.destroy?.(); } catch (e) { /* already gone */ }
      vadRef.current = null;
    }
  }, []);

  // --- the session ---------------------------------------------------

  if (!sessionRef.current) {
    sessionRef.current = createListeningSession({
      onState: (next) => setSessionState(next),
      onEnd: (reason) => setEndReason(reason),
      // A constant in every build without the test hook — see
      // `voiceTestSilenceMs`, which folds to `SILENCE_TIMEOUT_MS`.
      silenceMs: voiceTestSilenceMs,
    });
  }

  const sendSegment = useCallback(async (blob, seconds) => {
    if (!blob || !blob.size) {
      sessionRef.current.dispatch('turn_done');
      return;
    }
    try {
      await onVoiceMessage?.(blob, Math.max(1, Math.round(seconds)));
    } catch (err) {
      // A failed turn NEVER closes the session: the keeper is mid-round
      // and a dropped request is not a reason to make them tap again.
      setError(`That one did not go through — ${err.message}`);
    } finally {
      sessionRef.current.dispatch('turn_done');
    }
  }, [onVoiceMessage]);

  /**
   * A finished segment: encode and send it.
   *
   * `audio` is the detector's own Float32Array — 16 kHz mono, WITH its
   * pre-speech padding — so nothing of the sentence is missing.
   */
  const handleSegment = useCallback((audio) => {
    if (sessionRef.current.dispatch('speech_end', { samples: audio }) !== THINKING) {
      return;
    }
    sendSegment(encodeWav(audio), secondsOf(audio));
  }, [sendSegment]);

  const startSession = useCallback(async () => {
    const session = sessionRef.current;
    setError(null);
    setEndReason(null);
    session.dispatch('start');

    // THE DETECTOR'S THREE CALLBACKS, AS ONE OBJECT. Spread into
    // `MicVAD.new` below and handed WHOLE to the test fake, so the
    // simulated path cannot drift into calling something the real
    // detector does not. There is one `onSpeechEnd` in this file.
    const vadCallbacks = {
      onSpeechStart: () => {
        sessionRef.current.dispatch('speech_start');
      },
      onSpeechEnd: (audio) => handleSegment(audio),
      onVADMisfire: () => {
        sessionRef.current.dispatch('speech_cancel');
      },
    };
    const testHook = voiceTestHook();

    try {
      // ── THE SIMULATED PATH (S3-06.3c) — NOT IN A PROD BUILD ───────
      //
      // It replaces exactly two things, the microphone and the
      // detector, and then rejoins the real path at `ready`. Everything
      // after this line — the state machine, the WAV, the upload,
      // Whisper, the turn, the read-back, Undo — is the same code the
      // operator runs.
      if (testHook) {
        const fake = createVoiceTestVad(testHook, vadCallbacks);
        if (!fake) throw new Error('this bundle has no simulated-voice support');
        streamRef.current = fake.stream;
        vadRef.current = fake;
        await fake.start();
        session.dispatch('ready');
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('This browser cannot open a microphone (needs HTTPS).');
      }
      // OUR gesture, OUR stream. iOS Safari needs one user gesture per
      // page load and this tap is it.
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      // THE LAZY LOAD. Anything that goes wrong here — an offline chunk,
      // a browser without WASM, an asset that did not get copied — is a
      // DEGRADE, never a dead mic: the session closes, the button goes
      // back to tap-to-record, and the operator is told which one they
      // have.
      try {
        const mod = await loadVad();
        const MicVAD = mod.MicVAD || mod.default?.MicVAD;
        if (!MicVAD) throw new Error('vad module has no MicVAD');
        vadRef.current = await MicVAD.new({
          // ⚠️ `getStream`, not `stream` — see the header. It hands back
          // the track this component already opened rather than letting
          // the library open a second one.
          getStream: async () => stream,
          // The library's defaults STOP the tracks on pause; ours are
          // owned by `stopTracks` and by the session's teardown.
          pauseStream: async () => {},
          resumeStream: async () => stream,
          // Pinned rather than inherited: the package's own
          // DEFAULT_MODEL is "legacy", and which model it fetches must
          // be OUR decision, matched by the asset copy script.
          model: 'v5',
          // OUR ORIGIN. Both paths, because the model/worklet and the
          // onnxruntime wasm come out of two different packages.
          baseAssetPath: VAD_ASSET_PATH,
          onnxWASMBasePath: VAD_ASSET_PATH,
          ...vadCallbacks,
        });
        await vadRef.current.start();
      } catch (err) {
        console.warn('[VoiceRecorder] VAD unavailable:', err?.message);
        setVadUnavailable(true);
        stopTracks();
        teardownVad();
        sessionRef.current.dispatch('start_failed', { reason: 'no_vad', error: err });
        setError('Hands-free listening is not available here — '
          + 'tap the mic to record one message at a time.');
        return;
      }

      session.dispatch('ready');
    } catch (err) {
      stopTracks();
      teardownVad();
      session.dispatch('start_failed', { reason: 'mic', error: err });
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone permission denied. Allow it and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found.');
      } else {
        setError(err.message || 'Could not open the microphone.');
      }
    }
  }, [handleSegment, stopTracks, teardownVad]);

  const stopSession = useCallback((reason) => {
    pttRef.current = false;
    teardownVad();
    stopTracks();
    sessionRef.current.dispatch('stop', { reason });
  }, [stopTracks, teardownVad]);

  // The session ENDING for any reason tears the audio down. One place,
  // so a two-minute silence close and a tap close leave the same state.
  useEffect(() => {
    if (sessionState !== ENDED) return;
    pttRef.current = false;
    teardownVad();
    stopTracks();
  }, [sessionState, stopTracks, teardownVad]);

  // `visibilitychange` — PAUSE, and stop the track. iOS suspends it
  // anyway; what matters is that the UI stops claiming to listen.
  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) return;
      if (!sessionRef.current) return;
      pttRef.current = false;
      teardownVad();
      stopTracks();
      sessionRef.current.dispatch('hidden');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [stopTracks, teardownVad]);

  // ── THE BLUETOOTH CLICKER — DESIGNED, UNVERIFIED ────────────────────
  //
  // A remote in KEYBOARD mode sends Enter or Space. While the dock has
  // focus and no text field does, that is push-to-talk: it asks the
  // detector to treat this as speech now and to close the segment on
  // release.
  //
  // ⚠️ IT MUST NOT EAT THE MIC BUTTON'S OWN KEYS. Space and Enter are
  // how a keyboard user presses a button, and the mic button is the only
  // way to STOP a session — hijacking them there left that user with a
  // mic they could not close. `event.target` being the button is the one
  // case this handler stands down for.
  //
  // `repeat` is ignored: a held key fires keydown over and over and each
  // one would open another segment.
  useEffect(() => {
    if (!wantsConversation) return undefined;
    const node = rootRef.current;
    if (!node) return undefined;

    const isTextField = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };
    const isOwnButton = (el) => !!el && !!micBtnRef.current
      && (el === micBtnRef.current || micBtnRef.current.contains(el));

    const onDown = (event) => {
      if (event.repeat) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (isTextField(event.target) || isOwnButton(event.target)) return;
      const state = sessionRef.current.state;
      if (state !== LISTENING && state !== HEARING) return;
      event.preventDefault();
      pttRef.current = true;
      sessionRef.current.dispatch('speech_start');
    };
    const onUp = (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (isTextField(event.target) || isOwnButton(event.target)) return;
      if (!pttRef.current) return;
      event.preventDefault();
      pttRef.current = false;
      // The DETECTOR still owns the audio: releasing the clicker ends
      // the push, and the segment arrives through `onSpeechEnd` like any
      // other. Nothing here fabricates a recording.
      if (sessionRef.current.state === HEARING) {
        sessionRef.current.dispatch('speech_cancel');
      }
    };

    node.addEventListener('keydown', onDown);
    node.addEventListener('keyup', onUp);
    return () => {
      node.removeEventListener('keydown', onDown);
      node.removeEventListener('keyup', onUp);
    };
  }, [wantsConversation]);

  // --- tap-to-record, the desk path ----------------------------------

  const stopRecording = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  }, []);

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('getUserMedia not supported - requires HTTPS');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;

      // iOS first — see the header. This used to hard-code webm.
      const mimeType = pickMimeType(
        typeof MediaRecorder !== 'undefined'
          ? MediaRecorder.isTypeSupported?.bind(MediaRecorder) : null);
      const recorder = new MediaRecorder(
        stream, mimeType ? { mimeType, audioBitsPerSecond: 128000 } : undefined);
      mediaRecorderRef.current = recorder;
      audioChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current || [],
          mimeType ? { type: mimeType } : undefined);
        const finalDuration = recordingTimeRef.current;
        stopTracks();
        if (!audioBlob || audioBlob.size === 0 || finalDuration === 0) {
          setError('No audio captured. Hold and speak for a second or two.');
          audioChunksRef.current = [];
          recordingTimeRef.current = 0;
          setRecordingTime(0);
          return;
        }
        if (onVoiceMessage) {
          setIsProcessing(true);
          try {
            await onVoiceMessage(audioBlob, finalDuration);
          } catch (err) {
            setError(`Failed to send voice message: ${err.message}`);
          } finally {
            setIsProcessing(false);
          }
        }
        audioChunksRef.current = [];
        recordingTimeRef.current = 0;
        setRecordingTime(0);
      };

      recorder.start(100);
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      timerIntervalRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= maxDurationSeconds) {
            stopRecording();
            return maxDurationSeconds;
          }
          return newTime;
        });
      }, 1000);
    } catch (err) {
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Microphone permission denied. Allow microphone access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found.');
      } else {
        setError(`Recording failed: ${err.message}`);
      }
    }
  }, [maxDurationSeconds, onVoiceMessage, stopRecording, stopTracks]);

  // Cleanup on unmount — never leave a track or a timer behind.
  useEffect(() => () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* gone */ }
    }
    if (vadRef.current) {
      try { vadRef.current.pause?.(); } catch (e) { /* gone */ }
      try { vadRef.current.destroy?.(); } catch (e) { /* gone */ }
      vadRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    sessionRef.current?.destroy();
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // --- render ---------------------------------------------------------

  const live = sessionState === LISTENING || sessionState === HEARING
    || sessionState === THINKING;
  const useConversation = wantsConversation && !vadUnavailable;

  const onMicTap = () => {
    if (!useConversation) {
      if (isRecording) stopRecording(); else startRecording();
      return;
    }
    // ANY tap while a session is open ENDS it (proposal §3f).
    if (live || sessionState === STARTING) { stopSession('tapped'); return; }
    if (sessionState === PAUSED) {
      sessionRef.current.dispatch('resume');
      startSession();
      return;
    }
    startSession();
  };

  const micLabel = () => {
    if (!useConversation) return isRecording ? formatTime(recordingTime) : 'Voice';
    // ⚠️ THE UPLOAD AND THE TURN ARE ONE WAIT (operator, phone pass
    // 2026-09-13). Mid-upload this component used to REPLACE the button
    // with a "Sending…" spinner of its own, so a single voice turn said
    // "Sending…" and then "Thinking" in the same 40px slot, two words
    // for one wait that the keeper cannot act on differently. In
    // conversation mode the button stays put and says THINKING for all
    // of it. Tap-to-record keeps its own span (it has no state label).
    if (isProcessing) return STATE_LABELS[THINKING];
    if (sessionState === IDLE || sessionState === ENDED) return 'Talk';
    // PAUSED gets TWO controls (S3-06.4), so the mic button says what it
    // does rather than what the state is: "Paused — tap to resume" is
    // the label for a state with one exit, and this one has two.
    if (sessionState === PAUSED) return 'Resume';
    return STATE_LABELS[sessionState];
  };

  /**
   * ⚠️ THE ONE STATUS TOKEN, REPORTED UP (operator ruling, phone pass on
   * beta 2026-09-13). The collapsed dock said the same thing three times
   * at once — "Working" in the composer, "Thinking" here, "Thinking…" on
   * a spinner row below — on the narrowest screen in the app. The mic
   * label wins, because it is already IN the composer row and therefore
   * free, and the panel switches its own row off for as long as this is
   * non-null.
   *
   * NULL is the important half: `idle`, `ended` and `paused` say "Talk"
   * and "Resume", which are AFFORDANCES, not status — a panel that stood
   * its spinner down for them would answer a typed turn with nothing at
   * all. So is tap-to-record, which has no session and no label of its
   * own.
   */
  const statusLabel = (useConversation
    && (isProcessing || (sessionState !== IDLE && sessionState !== ENDED
      && sessionState !== PAUSED)))
    ? micLabel()
    : null;

  // Through a ref, so the effect depends on the LABEL and not on the
  // parent's render count: `onStatusChange` is an ordinary prop and a
  // frame that rebuilt it every render would otherwise re-report on
  // every render of the chat.
  const statusCbRef = useRef(onStatusChange);
  useEffect(() => { statusCbRef.current = onStatusChange; }, [onStatusChange]);
  useEffect(() => { statusCbRef.current?.(statusLabel); }, [statusLabel]);
  // And on the way out: a dock that unmounts mid-session must not leave
  // the panel believing the mic is still saying something.
  useEffect(() => () => statusCbRef.current?.(null), []);

  /** End a paused session outright. ONE tap, without reopening the mic. */
  const onEndPaused = () => {
    stopSession('ended_from_pause');
  };

  return (
    <div
      className={`voice-recorder${compact ? ' voice-recorder--compact' : ''}`}
      ref={rootRef}
      /* The clicker's keys have to reach this subtree, and a div is not
         focusable without this. -1 so it is not in the Tab order: the
         operator reaches it by tapping the mic, and a keyboard user
         still gets the button itself. */
      tabIndex={-1}
      data-voice-state={sessionState}
      data-voice-mode={useConversation ? 'conversation' : 'tap'}
    >
      {error && (
        <Alert
          variant="warning"
          dismissible
          onClose={() => setError(null)}
          className="voice-recorder-alert mb-1 py-1 px-2"
        >
          <small>{error}</small>
        </Alert>
      )}

      {endReason === 'silence' && sessionState === ENDED && (
        <div className="voice-recorder-hint" role="status">
          <small>Two minutes of quiet — the session closed.</small>
        </div>
      )}

      {/* ⚠️ THE SPINNER SPAN IS THE DESK'S, NOT THE ROUND'S (operator,
          2026-09-13). Tap-to-record has no state label, so mid-upload it
          still swaps the button for "Sending…". CONVERSATION mode keeps
          the BUTTON — the label already says Thinking…, and replacing
          the control the keeper taps to stop the session with a spinner,
          in the middle of a turn, takes the stop away exactly when a
          mis-heard sentence is being sent. */}
      {isProcessing && !useConversation ? (
        <span className="voice-recorder-processing">
          <Spinner animation="border" size="sm" className="me-1" />
          <small>Sending…</small>
        </span>
      ) : (
        <button
          type="button"
          ref={micBtnRef}
          onClick={onMicTap}
          disabled={disabled && !live}
          /* J (batch 8) — THE TALK BUTTON GETS COLOUR. It shipped as a
             bare `.btn`: no variant, no fill, the same weight as the
             page behind it, next to a filled blue Send. It is the
             primary CTA of the one surface this sprint exists for, so
             it takes the app's primary-CTA class — `btn-blue`, Froggy
             Blue with BLACK text (the standing rule; `--ff-brand-primary`
             is a cyan that white text fails WCAG on).
             A LIVE session keeps its own look instead: an open
             microphone is a state to stop, not a call to action.
             Batch 34 review NIT: on the customer surfaces
             (variant="secondary") the live mic KEEPS the family classes
             and only adds `is-live`, so the bubble's compaction rule
             (`.ff-btn.voice-recorder-btn`) still sizes it while
             recording. The dock's class strings are unchanged.
             R-UI-LEFTOVERS B3 (2026-11-03): the dock's mic moves onto the
             family too — at rest `ff-btn--primary` (the same Froggy blue,
             #111 text: the composer's ONE primary), live the family base
             plus `is-live` (VoiceRecorder.css keeps its red). */
          className={variant === 'secondary'
            ? `ff-btn ff-btn--secondary voice-recorder-btn${(live || isRecording) ? ' is-live' : ''}`
            : ((live || isRecording)
              ? 'ff-btn voice-recorder-btn is-live'
              : 'ff-btn ff-btn--primary voice-recorder-btn')}
          aria-label={useConversation
            ? (live ? 'End the listening session' : 'Start a listening session')
            : (isRecording ? 'Stop recording' : 'Record a voice message')}
          aria-pressed={live || isRecording}
          data-testid="voice-recorder-btn"
          title={useConversation
            ? 'One tap opens the mic and keeps it open. Tap again to stop.'
            : `Record a voice message (up to ${Math.round(maxDurationSeconds / 60)} minutes)`}
        >
          <i className="bi bi-mic-fill" aria-hidden="true" />
          <span className="voice-recorder-label">{micLabel()}</span>
        </button>
      )}

      {/* J — the second exit from PAUSED. Without it, closing the mic
          means resuming it first: two taps, and the second of them
          reopens the microphone the keeper is trying to close. */}
      {useConversation && sessionState === PAUSED && !isProcessing && (
        <button
          type="button"
          onClick={onEndPaused}
          className="ff-btn ff-btn--secondary voice-recorder-end"
          aria-label="End the listening session"
          data-testid="voice-recorder-end"
          title="Close the microphone"
        >
          End
        </button>
      )}

      {!useConversation && isRecording
        && recordingTime >= recommendedMaxSeconds
        && recordingTime < maxDurationSeconds && (
        <div className="voice-recorder-hint">
          <small className="text-warning">Longer messages take longer to process</small>
        </div>
      )}
    </div>
  );
};

VoiceRecorder.propTypes = {
  onVoiceMessage: PropTypes.func,
  /**
   * The ONE status token: called with what the mic button is currently
   * SAYING, or null when it is saying nothing the keeper should read as
   * status (tap-to-record, or a session that is idle, ended or paused).
   * The panel stands its own spinner row down while this is set.
   */
  onStatusChange: PropTypes.func,
  disabled: PropTypes.bool,
  /** The dock's layout: a single small control, no debug panel. */
  compact: PropTypes.bool,
  /** Override the mode. Defaults to `compact` — a desk keeps tap-to-record. */
  conversation: PropTypes.bool,
  maxDurationSeconds: PropTypes.number,
  recommendedMaxSeconds: PropTypes.number,
};

VoiceRecorder.defaultProps = {
  onVoiceMessage: undefined,
  onStatusChange: undefined,
  disabled: false,
  compact: false,
  conversation: undefined,
  maxDurationSeconds: 300,
  recommendedMaxSeconds: 75,
};

export default VoiceRecorder;
